/* eslint-disable no-shadow */
/* eslint-disable import/no-cycle */
import ApiService from '@/services/api.service';
import { apiEndpoint } from '@/shared';
import { ValidationError } from '@/errors';

const endpoint = `${apiEndpoint}/inventory/settings`;

const state = {
  data: null,
  automatedClosure: null,
  update: null,
  pending: false,
  automatedClosurePending: false,
  error: null,
  updatePending: false,
  updateError: null,
  showDefaultCancellationRules: false,
};

const getters = {
  loaded: (state) => !state.pending && state.data != null,
};

const mutations = {
  clearErrors(state) {
    state.error = null;
    state.updateError = null;
  },
  beforeLoading(state, initial = false) {
    if (initial) {
      state.data = null;
    }
    state.error = null;
    state.pending = true;
  },
  beforeUpdate(state) {
    state.updateError = null;
    state.updatePending = true;
  },
  error(state, error) {
    state.pending = false;
    state.automatedClosurePending = false;
    state.error = error;
  },
  data(state, data) {
    state.pending = false;
    state.data = data;
  },
  beforeAutomatedClosure(state) {
    state.automatedClosurePending = true;
  },
  afterAutomatedClosure(state) {
    state.automatedClosurePending = false;
  },
  automatedClosure(state, data) {
    state.automatedClosurePending = false;
    state.automatedClosure = data;
  },
  update(state, update = {}) {
    state.update = update;
  },
  afterUpdate(state) {
    state.pending = false;
    state.updatePending = false;
  },
  setDefaultCancellationRules(state, value = true) {
    state.showDefaultCancellationRules = value;
  },
};

function handleApiError(commit, error) {
  const { status, data } = error.response;
  let err = error;
  switch (status) {
    case 422:
    case 400:
      err = new ValidationError(status, data.message, data.errors);
      break;
    default:
      break;
  }
  commit('update', err);
  throw err;
}

const actions = {
  async fetchData({ commit }, forced = false) {
    commit('beforeLoading', forced);
    try {
      const response = await ApiService.get(`${endpoint}`);
      commit('data', response.data);
    } catch (error) {
      commit('error', error);
      throw handleApiError(commit, error);
    }
  },
  async fetchAutomatedClosureData({ commit }, payload) {
    commit('beforeAutomatedClosure');
    try {
      const response = await ApiService.post(`${endpoint}/get-automated-closure`, payload);
      commit('automatedClosure', response.data.data.autoclosure);
    } catch (error) {
      commit('error', error);
      throw handleApiError(commit, error);
    }
  },
  async updateAutomatedClosureData({ commit }, payload) {
    commit('beforeAutomatedClosure');
    try {
      await ApiService.post(`${endpoint}/automated-closure`, payload);
      commit('afterAutomatedClosure');
    } catch (error) {
      commit('error', error);
      throw handleApiError(commit, error);
    }
  },
  async deleteAutomatedClosureData({ commit }, payload) {
    commit('beforeAutomatedClosure');
    try {
      const data = await ApiService.post(`${endpoint}/delete-automated-closure`, payload);
      commit('afterAutomatedClosure');
      return data;
    } catch (error) {
      commit('error', error);
      throw handleApiError(commit, error);
    }
  },
  async updateData({ commit }, autoReplenishment) {
    commit('beforeUpdate');
    try {
      await ApiService.put(`${endpoint}`, { AutoReplenishment: autoReplenishment });
      commit('update', autoReplenishment);
      commit('afterUpdate');
    } catch (error) {
      commit('error', error);
      throw handleApiError(commit, error);
    }
  },
  async updateDefaultCancellationRulesSettings({ commit }, status) {
    commit('beforeUpdate');
    try {
      await ApiService.put(`${endpoint}/cancellation-rules/${status}`);
      commit('setDefaultCancellationRules', status);
      commit('afterUpdate');
    } catch (error) {
      commit('error', error);
      throw handleApiError(commit, error);
    }
  },
  async fetchMinLosSetting({ commit }) {
    try {
      const { data } = await ApiService.get(`${endpoint}/min-los`);
      return data;
    } catch (error) {
      commit('error', error);
      throw handleApiError(commit, error);
    }
  },
  async updateMinLosSetting({ commit }, payload) {
    try {
      const { data } = await ApiService.post(`${endpoint}/min-los`, payload);
      return data;
    } catch (error) {
      commit('error', error);
      throw handleApiError(commit, error);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
